import { useCallback, useState } from 'react'
import { ListOption } from '../organisms/list/list-option/list-option.type'
import { useSearchString } from './use-search-string.web'
import { findIndexOfOption } from '../utils/find-index-of-option'
import { useDropdownFocus } from './use-dropdown-focus.web'

export const useDropdownState = (props: {
  options: ListOption[]
  isAccessible: boolean
  isOpen: boolean
  activeValue: string
}) => {
  const { options, isAccessible, isOpen, activeValue } = props
  const [isModalOpen, setOpenModal] = useState(isAccessible ? isOpen : false)
  const { getSearchResult } = useSearchString(options)
  const activeValueIndex = findIndexOfOption(options, activeValue)
  const { focusedOptionIndex, setFocusedOptionIndex, moveFocusUp, moveFocusDown } = useDropdownFocus(options)

  // don't open dropdown if it's readonly or disabled
  const toggleModal = useCallback(
    (state: boolean) => {
      if (isAccessible) {
        setOpenModal(state)
      }
    },
    [isAccessible]
  )

  const handleClose = useCallback(() => {
    toggleModal(false)
    setFocusedOptionIndex(activeValueIndex)
  }, [activeValueIndex, setFocusedOptionIndex, toggleModal])

  const handleOpen = useCallback(() => {
    toggleModal(true)
    setFocusedOptionIndex(activeValueIndex)
  }, [activeValueIndex, setFocusedOptionIndex, toggleModal])

  const onClickHandler = useCallback(() => {
    if (isModalOpen) {
      handleClose()
    } else {
      handleOpen()
    }
  }, [isModalOpen, handleClose, handleOpen])

  const handleAlphanumericKeyPress = useCallback(
    (keyValue: string) => {
      // set focus to the matched search string
      const { isMatchFound, matchedStringIndex } = getSearchResult(keyValue)
      if (isMatchFound && !isModalOpen) toggleModal(true)
      setFocusedOptionIndex(matchedStringIndex)
    },
    [getSearchResult, isModalOpen, setFocusedOptionIndex, toggleModal]
  )

  return {
    isModalOpen,
    focusedOptionIndex,
    handleAlphanumericKeyPress,
    onClickHandler,
    toggleModal,
    handleOpen,
    handleClose,
    moveFocusDown,
    moveFocusUp,
    setFocusedOptionIndex,
  }
}
